import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import PatientService from "../service/PatientService";
import editPencil from "assets/icons/edit-pencil.svg";
import UploadDocumentModal from "./UploadDocumentModal";
import usePrescriptionDoctors from "../helper/usePrescriptionDoctors";
import {
  PINNED_FOLDERS,
  categoryLabel,
  categoryOrderIndex,
  docIdLabel,
  formatDocDate,
  formatDocTime,
  formatLabel,
  formatTone,
  isPrescriptionDoc,
  normalizeDocGroups,
  openDocument,
} from "../helper/documentConstants";

/**
 * The header's "Documents · View all" control. TWO LEVELS, per Figma
 * 3023:9382 (folder grid) and 3023:9415 (one folder's documents):
 *
 *   level 1  a 2-column grid of folder tiles — icon, name, "4 documents"
 *   level 2  that folder's files — type badge, name, date, open chevron
 *
 * Both levels are drawn from ONE response. `GET /v1/documents/patients/{id}
 * ?groupBy=type` already arrives grouped, with each folder's documents inlined
 * (`all`), so opening a folder is a state change and never a second request.
 * There IS a per-folder endpoint; using it would be a round-trip for data we
 * are already holding.
 *
 * A doc token is minted in the background on open so a row click can build the
 * tokenised URL synchronously — `window.open` inside an async callback is what
 * popup blockers eat.
 */

const ChevronDown = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 11 6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 1L5.5 4.6 10 1" />
  </svg>
);
ChevronDown.propTypes = { className: PropTypes.string };

/** Figma's own "chev . open" vector, 3023:9426 — exported verbatim. */
const ChevronRight = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 6.1 10.6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M0.8 0.8L5.3 5.3L0.8 9.8" />
  </svg>
);
ChevronRight.propTypes = { className: PropTypes.string };

/**
 * Edit details (title / description / folder).
 *
 * THE APP'S OWN GLYPH, not a hand-drawn one: this is the same
 * assets/icons/edit-pencil.svg the login screen puts beside the mobile number
 * (Figma 3015:9110). A second pencil of my own drawing read as a different
 * control at a glance (Nittin, 2026-09-12). The asset carries its own #f97f0f
 * fill, so nothing here tints it.
 */
const PencilIcon = () => <img className="ph-doc-edit-ico" src={editPencil} alt="" />;

/** The same vector mirrored — the board has no back control to export. */
const ChevronLeft = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 6.1 10.6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5.3 0.8L0.8 5.3L5.3 9.8" />
  </svg>
);
ChevronLeft.propTypes = { className: PropTypes.string };

/**
 * The popup's upload control (3023:9382, asset a4093e93) — Figma's own vector,
 * exported verbatim. It is a FILLED glyph, unlike the stroked close beside it,
 * and the board draws it in a darker grey (#101828 vs the close's #6A7282);
 * both are recoloured to currentColor so the SCSS owns that.
 */
const UploadIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M11 16V7.85L8.4 10.45L7 9L12 4L17 9L15.6 10.45L13 7.85V16H11ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z" />
  </svg>
);
UploadIcon.propTypes = { className: PropTypes.string };

/** Figma's close, 3023:9387 — exported verbatim (was a text "x" before). */
const CloseIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M7 7L17 17M17 7L7 17" />
  </svg>
);
CloseIcon.propTypes = { className: PropTypes.string };

/**
 * The folder mark (3023:9391 filled / 3023:9404 empty). NOT a vector on the
 * board — Figma draws it as two rounded rectangles, so it is two rects here
 * too: a 16x10 r3 tab, and a 38x25 r5 face with a 1px stroke, the face inset
 * 5px from the top. Colours come from the SCSS so the empty variant is a
 * class, not a second copy of the geometry.
 */
const FolderIcon = ({ empty }) => (
  <svg
    className={`ph-doc-tile-icon${empty ? " is-empty" : ""}`}
    viewBox="0 0 38 30"
    fill="none"
    aria-hidden="true"
  >
    <rect className="ph-doc-tile-icon-tab" x="0" y="0" width="16" height="10" rx="3" />
    <rect
      className="ph-doc-tile-icon-face"
      x="0.5"
      y="5.5"
      width="37"
      height="24"
      rx="5"
    />
  </svg>
);
FolderIcon.propTypes = { empty: PropTypes.bool };

const DocumentsMenu = ({ patientId, variant, uploadRequested, onUploadHandled }) => {
  /* MOBILE ONLY. `inline` draws the same two levels with no trigger and no
     popover — the Documents tab of the phone's patient screen is the panel, so
     there is nothing to pop over. Upload is asked for from outside (the tab's
     own "+ Upload" bar) through `uploadRequested`, since the board puts that
     button at the foot of the screen rather than in a header. Desktop passes
     neither prop and is unchanged. */
  const inline = variant === "inline";
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  /* The document being edited, or null. Same modal, different mode. */
  const [editDoc, setEditDoc] = useState(null);
  /* The dismissal is GATED while EITHER modal is up. usePopover closes on
     any document mousedown outside the menu, on any scroll (capture phase, so
     the modal cannot stop it) and on Escape — and the modal's backdrop, its
     scrolling body and its Escape all qualify. Ungated, the grid underneath
     would tear down mid-upload and the refreshed folders would not be there to
     see afterwards. */
  const { triggerRef, menuRef, menuStyle } = usePopover(open && !inline, () => {
    if (!uploadOpen && !editDoc) setOpen(false);
  });
  const service = useRef(new PatientService()).current;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [groups, setGroups] = useState(null); // [{ type, all }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadedRef = useRef(false); // a successful load happened — don't refetch
  /** null = the folder grid; a type = that folder's documents. */
  const [openType, setOpenType] = useState(null);

  // Every open starts at the grid, never inside whichever folder was last read.
  useEffect(() => {
    if (open) setOpenType(null);
  }, [open]);

  /* The tab's "+ Upload" — the same modal the header's icon opens. Handed back
     at once so the flag cannot re-fire on the next render. */
  useEffect(() => {
    if (!inline || !uploadRequested) return;
    setUploadOpen(true);
    if (onUploadHandled) onUploadHandled();
  }, [inline, uploadRequested, onUploadHandled]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    service.getPatientDocuments(
      patientId,
      (res) => {
        if (!mounted.current) return;
        loadedRef.current = true;
        setGroups(normalizeDocGroups(res));
        setLoading(false);
      },
      () => {
        if (!mounted.current) return;
        setError(true);
        setLoading(false);
      }
    );
  }, [patientId, service]);

  useEffect(() => {
    if (!open && !inline) return;
    // Fetch once — a successful load sticks (loadedRef), so reopening shows the
    // cached list without a network round-trip or a loading flash. Retry calls
    // load() directly, bypassing this guard.
    if (!loadedRef.current) load();
    // Mint the doc token in the background so a row click can open synchronously.
    service.generateDocToken(
      () => {},
      () => {}
    );
  }, [open, inline, load, service]);

  /**
   * The tiles: every folder that HAS documents, plus the four the board always
   * draws even when empty. Sorted into the shared category order so the grid
   * reads the same whatever a given patient happens to hold.
   */
  const withDocs = (groups || []).filter((g) => g && g.all && g.all.length);
  const seen = withDocs.reduce((acc, g) => ({ ...acc, [g.type]: true }), {});
  const tiles = withDocs
    .concat(PINNED_FOLDERS.filter((t) => !seen[t]).map((t) => ({ type: t, all: [] })))
    .sort((a, b) => categoryOrderIndex(a.type) - categoryOrderIndex(b.type));

  const total = withDocs.reduce((n, g) => n + g.all.length, 0);
  /* Each prescription PDF's doctor, for its row's label. Fetched once, as soon as an open list
     holds a prescription, so the names are usually in before the folder is. */
  const doctorNames = usePrescriptionDoctors(
    patientId,
    (open || inline) && withDocs.some((g) => g.all.some(isPrescriptionDoc))
  );
  const folder = openType == null ? null : tiles.find((g) => g.type === openType);
  // `createdAt desc` is the server's own order and what the date column shows;
  // sorting on updatedAt here would put the list out of step with its dates.
  const files = folder ? folder.all : [];

  const body = () => {
    if (loading) return <p className="ph-doc-msg">Loading documents&hellip;</p>;
    if (error) {
      return (
        <p className="ph-doc-msg">
          Couldn&rsquo;t load documents.{" "}
          <button type="button" className="ph-doc-retry" onClick={load}>
            Retry
          </button>
        </p>
      );
    }

    if (folder) {
      return (
        <div className="ph-doc-files">
          {files.map((d) => (
            /* A CONTAINER, not the button it used to be. A per-row control
               cannot live inside a <button>, and the alternative — an onClick
               on a <span> — is unreachable by keyboard. The open action keeps
               its own button, so both are real controls and the row still
               lights up as one strip on hover. */
            <div className="ph-doc-row" key={d.id}>
              <button
                type="button"
                className="ph-doc-open"
                onClick={() => openDocument(d)}
              >
                <span className={`ph-doc-badge is-${formatTone(d.format)}`}>
                  {formatLabel(d.format)}
                </span>
                {/* The document's own id over its title (Figma 3023:9415); a long
                    title is what the ellipsis takes. The {" "}s are for the TEXT
                    (copy, screen readers: "23918 report.pdf") — between flex
                    items they draw nothing, and the gap does the spacing. A
                    prescription's id carries its doctor: "29771 (Dr.Karishma)". */}
                <span className="ph-doc-label">
                  <span className="ph-doc-id">{docIdLabel(d, doctorNames)}</span>{" "}
                  <span className="ph-doc-name">{d.title || "Untitled"}</span>
                </span>
                {/* Date over time, both IST (formatDocDate/formatDocTime). */}
                <span className="ph-doc-when">
                  <span className="ph-doc-date">{formatDocDate(d.createdAt || d.updatedAt)}</span>{" "}
                  <span className="ph-doc-time">{formatDocTime(d.createdAt || d.updatedAt)}</span>
                </span>
                <ChevronRight className="ph-doc-chev" />
              </button>
              <button
                type="button"
                className="ph-doc-edit"
                title="Edit details"
                /* Named for the document, since a panel of these is otherwise
                   a column of identical "Edit" buttons to a screen reader — and
                   by its id too, since titles repeat ("Test Document (1).pdf"). */
                aria-label={`Edit ${d.id} ${d.title || "document"}`}
                onClick={() => setEditDoc(d)}
              >
                <PencilIcon />
              </button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="ph-doc-grid">
        {tiles.map((g) => {
          const n = g.all.length;
          return (
            <button
              type="button"
              key={g.type}
              className={`ph-doc-tile${n ? "" : " is-empty"}`}
              // An empty folder is drawn but inert — opening it would show a
              // blank pane the doctor then has to back out of.
              disabled={!n}
              onClick={() => setOpenType(g.type)}
            >
              <FolderIcon empty={!n} />
              <span className="ph-doc-tile-name">{categoryLabel(g.type)}</span>
              <span className="ph-doc-tile-count">
                {n === 0 ? "No documents" : `${n} document${n === 1 ? "" : "s"}`}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  /* Shared by both returns. OUTSIDE the {open && …} block on purpose: the
     popover's subtree is conditional, so a modal rendered within it would
     vanish the moment anything closed the menu.
     ONE MOUNT, two modes: `document` set means "Edit document" — the same
     Title / Description / folder fields, prefilled, with no file. */
  const modals = (uploadOpen || editDoc) && (
    <UploadDocumentModal
      patientId={patientId}
      editDocument={editDoc || undefined}
      /* The folder counts in its picker come from the same grouping this
         popup draws its tiles from. */
      groups={groups}
      onClose={() => {
        setUploadOpen(false);
        setEditDoc(null);
      }}
      onUploaded={() => {
        /* Both halves matter. load() refreshes the grid that is on screen;
           clearing the flag covers the case where the menu was dismissed
           while the modal was up, so the NEXT open shows the new document
           rather than the cached pre-upload grid. */
        loadedRef.current = false;
        load();
      }}
    />
  );

  /* MOBILE: the panel itself. The head is only drawn inside a folder, where it
     is the way back — at the grid the tab rail above already names the panel,
     and upload lives in the bar at the foot of the screen. */
  if (inline) {
    return (
      <div className="ph-doc-inline">
        {folder && (
          <div className="ph-doc-inline-head">
            <button
              type="button"
              className="ph-doc-back"
              onClick={() => setOpenType(null)}
              aria-label="Back to all documents"
            >
              <ChevronLeft className="ph-doc-back-chev" />
              <span className="ph-doc-menu-title">{categoryLabel(folder.type)}</span>
              <span className="ph-doc-menu-count">&middot; {files.length}</span>
            </button>
          </div>
        )}
        <div className={`ph-doc-menu-body${folder ? " is-files" : ""}`}>{body()}</div>
        {modals}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`ph-doc-all${variant === "cta" ? " is-cta" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {/* Two triggers, one menu. The default is phr's cell pill, "View all"
            under an ALL DOCUMENTS label (5125:4549). `cta` is the patient bar
            with no appointment context (6167:15533), where the label is gone
            and the button names itself — so it carries the words and drops
            the chevron. */}
        {variant === "cta" ? (
          "All documents"
        ) : (
          <>
            View all
            <ChevronDown className="ph-doc-all-chev" />
          </>
        )}
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="ph-doc-menu"
            style={{ top: menuStyle.top, left: menuStyle.left }}
            role="dialog"
            aria-label={folder ? categoryLabel(folder.type) : "All documents"}
          >
            <div className="ph-doc-menu-head">
              {/* Inside a folder the title doubles as the way back. The board
                  draws only a close ×, but it also never shows how to return to
                  the grid; a back affordance costs nothing and its absence
                  strands the doctor. */}
              {folder ? (
                <button
                  type="button"
                  className="ph-doc-back"
                  onClick={() => setOpenType(null)}
                  aria-label="Back to all documents"
                >
                  <ChevronLeft className="ph-doc-back-chev" />
                  <span className="ph-doc-menu-title">{categoryLabel(folder.type)}</span>
                  <span className="ph-doc-menu-count">&middot; {files.length}</span>
                </button>
              ) : (
                <>
                  <span className="ph-doc-menu-title">All documents</span>
                  {!loading && !error && (
                    <span className="ph-doc-menu-count">&middot; {total}</span>
                  )}
                </>
              )}
              {/* Upload (3023:9386's sibling) — the board puts it immediately
                  left of the close, in a darker grey than it. */}
              <button
                type="button"
                className="ph-doc-upload"
                aria-label="Upload document"
                title="Upload document"
                onClick={() => setUploadOpen(true)}
              >
                <UploadIcon className="ph-doc-upload-icon" />
              </button>
              <button
                type="button"
                className="ph-doc-menu-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <CloseIcon className="ph-doc-menu-close-icon" />
              </button>
            </div>

            <div className={`ph-doc-menu-body${folder ? " is-files" : ""}`}>{body()}</div>
          </div>,
          document.body
        )}

      {modals}
    </>
  );
};

DocumentsMenu.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** "pill" (default) — phr's labelled cell; "cta" — the standalone
      "All documents" button of the no-appointment patient bar (6167:15533);
      "inline" — MOBILE ONLY, the panel with no trigger and no popover. */
  variant: PropTypes.oneOf(["pill", "cta", "inline"]),
  /** Mobile `inline` only: flip true to open the upload modal, from the tab's
      own "+ Upload" bar. Handed back through onUploadHandled at once. */
  uploadRequested: PropTypes.bool,
  onUploadHandled: PropTypes.func,
};

export default DocumentsMenu;
